import React from 'react';
import Badge from '../components/ui/Badge';
import Card from '../components/ui/Card';
import ProductInterestForm from '../components/products/ProductInterestForm';

const STATUS_STYLES: Record<string, { label: string; className: string }> = {
  live: {
    label: 'Live',
    className: 'bg-[#EAF7EC] text-[#2F9A52]',
  },
  research: {
    label: 'In Research',
    className: 'bg-[#FFF5E6] text-[#B56B03]',
  },
  concept: {
    label: 'Concept',
    className: 'bg-[#F1EAFE] text-[#7130F0]',
  },
} as const;

const Products: React.FC = () => {
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="w-full bg-white">
      <section className="bg-white pb-10 pt-16">
        <div className="mx-auto max-w-[1360px] px-5">
          <div className="text-center">
            <Badge className="inline-flex rounded-full border border-[#B8B8B8] bg-white px-3 py-1 font-description text-[10px] font-normal text-[#9C9C9C]">
              Our Products
            </Badge>
            <h1 className="mt-3 font-title text-[42px] font-semibold leading-none text-[#212121] max-[700px]:text-[32px]">
              Products &amp; Projects
            </h1>
            <p className="mx-auto mt-2 max-w-[640px] font-description text-[12px] font-light leading-[1.4] text-[#919191]">
              We don't just build software for clients. We also research, develop and validate our own digital
              products, giving us first-hand experience of turning ideas into practical solutions.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-white pb-16">
        <div className="mx-auto max-w-[1360px] px-5">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 max-[980px]:grid-cols-1">
            {/* BaoAfrik - Live */}
            <Card className="flex flex-col rounded-[20px] border border-[#E4E4E4] bg-white p-6">
              <div className="flex items-center justify-between">
                <h2 className="font-title text-[26px] font-medium leading-tight text-[#212121]">BaoAfrik</h2>
                <span
                  className={`rounded-full px-3 py-1 font-description text-[10px] font-medium ${STATUS_STYLES.live.className}`}
                >
                  {STATUS_STYLES.live.label}
                </span>
              </div>
              <p className="mt-2 font-description text-[12px] font-normal leading-[1.4] text-[#919191]">
                Live product currently being validated and improved.
              </p>
              <p className="mt-4 font-description text-[14px] font-light leading-[1.55] text-[#616161]">
                A digital marketplace designed to improve the discovery and exchange of African products and
                services, beginning with the African diaspora market. BaoAfrik connects sellers across the
                continent with buyers locally and abroad — making it easier to find, trade, and support
                African-made goods.
              </p>
              <div className="mt-6 pt-2">
                <a
                  href="https://baoafrik.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-[#2776EA] px-6 py-2.5 font-title text-[13px] font-medium text-white transition-colors hover:bg-[#1a5cc7]"
                >
                  Explore BaoAfrik
                  <span aria-hidden="true">→</span>
                </a>
              </div>
            </Card>

            {/* Port2Market - Research */}
            <Card className="flex flex-col rounded-[20px] border border-[#E4E4E4] bg-white p-6">
              <div className="flex items-center justify-between">
                <h2 className="font-title text-[26px] font-medium leading-tight text-[#212121]">Port2Market</h2>
                <span
                  className={`rounded-full px-3 py-1 font-description text-[10px] font-medium ${STATUS_STYLES.research.className}`}
                >
                  {STATUS_STYLES.research.label}
                </span>
              </div>
              <p className="mt-2 font-description text-[12px] font-normal leading-[1.4] text-[#919191]">
                Product research and UI/UX design in progress.
              </p>
              <p className="mt-4 font-description text-[14px] font-light leading-[1.55] text-[#616161]">
                A trade visibility platform designed to connect exporters and buyers by showing what goods are
                arriving, when they are arriving, and where they can be accessed. Port2Market aims to bring
                transparency and efficiency to the port ecosystem.
              </p>
              <div className="mt-6 border-t border-[#E4E4E4] pt-4">
                <p className="font-description text-[12px] font-medium text-[#616161]">
                  Follow this project
                </p>
                <ProductInterestForm subject="Port2Market" ctaLabel="Join the Waitlist" />
              </div>
            </Card>

            {/* AfrikBooking - Concept */}
            <Card className="flex flex-col rounded-[20px] border border-[#E4E4E4] bg-white p-6">
              <div className="flex items-center justify-between">
                <h2 className="font-title text-[26px] font-medium leading-tight text-[#212121]">AfrikBooking</h2>
                <span
                  className={`rounded-full px-3 py-1 font-description text-[10px] font-medium ${STATUS_STYLES.concept.className}`}
                >
                  {STATUS_STYLES.concept.label}
                </span>
              </div>
              <p className="mt-2 font-description text-[12px] font-normal leading-[1.4] text-[#919191]">
                Concept prototype under evaluation.
              </p>
              <p className="mt-4 font-description text-[14px] font-light leading-[1.55] text-[#616161]">
                A configurable appointment and service-booking solution designed to support councils, hospitals,
                public institutions, schools, and other organisations that manage appointments, queues, and
                service requests.
              </p>
              <div className="mt-6 border-t border-[#E4E4E4] pt-4">
                <p className="font-description text-[12px] font-medium text-[#616161]">
                  Interested? Let us know
                </p>
                <ProductInterestForm subject="AfrikBooking" ctaLabel="Register Your Interest" />
              </div>
            </Card>
          </div>

          {/* <div className="mt-8 flex items-start gap-3 rounded-[16px] bg-[#F8FAFE] px-6 py-5 max-[760px]:flex-col max-[760px]:gap-2">
            <p className="font-description text-[13px] font-light leading-[1.55] text-[#616161]">
              <span className="font-medium text-[#212121]">Bao Academy</span> is a separate specialist brand and
              related venture focused on technology training and skills development.
            </p>
            <a
              href="https://baocademy.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#2776EA] px-5 py-2 font-title text-[12px] font-medium text-white transition-colors hover:bg-[#1a5cc7]"
            >
              Visit baocademy.com
              <span aria-hidden="true">→</span>
            </a>
          </div> */}
        </div>
      </section>
    </div>
  );
};

export default Products;
